import { useMemo, useState } from "react";
import {
  Bar,
  CartesianGrid,
  Cell,
  ComposedChart,
  LabelList,
  Line,
  ReferenceArea,
  ResponsiveContainer,
  Scatter,
  ScatterChart,
  Tooltip,
  XAxis,
  YAxis,
  ZAxis,
  type TooltipContentProps,
} from "recharts";
import type { InsightsTopCustomerRow } from "@shared-core/data/api-types";
import {
  formatGbpCompact,
  formatSignedPct,
  goneQuietCount,
  marginErodingCount,
  SEGMENT_FILTERS,
  SHARE_BASES,
  type SegmentFilter,
  type ShareBasis,
  steepDeclinerCount,
  strongGrowerCount,
  toParetoBars,
  toScatterPoints,
  toWatchList,
  topNRevenue,
  topNShare,
  totalRevenueTtm,
  type TopCustomerScatterPoint,
} from "@shared-core/domain/insights-top-customers";

// Top 100 customers — scale, momentum & margin. Faithful to the settled design
// reference (docs/features/bi-native-analysis/charts/top100-analytics.html): a
// scale-vs-momentum scatter (x = 12-month revenue, y = YoY growth, size = 90-day
// revenue) with a segment filter, watch-zone quadrant shading, bubble labels and
// a rich tooltip; a revenue Pareto with cumulative share (toggleable between the
// top 20 and whole-company revenue as the base); and a "big & shrinking"
// watch-list carrying QoQ + rep. Panel-for-panel with the mockup — see
// docs/features/bi-native-analysis/06_top100_fidelity_checklist.md.
//
// Server composes, client renders: the endpoint returns the top-N rows already
// ranked and enriched (segment/rep/market/QoQ/margin-move); these panels only
// project and filter them client-side by segment. No client-side sort or slice
// of the underlying league table.

const GROWER_COLOR = "var(--color-chart-2)"; // grower >50%
const UP_COLOR = "var(--color-chart-3)"; // up 0..50%
const DIP_COLOR = "var(--color-chart-5)"; // down 0..−40%
const DECLINE_COLOR = "var(--color-destructive)"; // steep <−40%
const REVENUE_COLOR = "var(--color-chart-1)";
const CUM_COLOR = "var(--color-chart-4)";

// YoY is capped on the y-axis so a handful of extreme movers don't flatten the
// rest; the true value stays in the tooltip.
const YOY_CAP = 250;
// The watch zone (mockup): big accounts (>£100k) that are shrinking (YoY < 0).
const WATCH_ZONE_REVENUE = 100_000;

/** Mockup's four momentum colour bands over YoY %. */
function bubbleColor(yoyPct: number): string {
  if (yoyPct > 50) return GROWER_COLOR;
  if (yoyPct > 0) return UP_COLOR;
  if (yoyPct > -40) return DIP_COLOR;
  return DECLINE_COLOR;
}

export interface TopCustomersChartProps {
  customers: InsightsTopCustomerRow[];
  /** TTM revenue over every reporting customer — the "share of total" base. */
  companyRevenueTtm: number;
}

export function TopCustomersChart({
  customers,
  companyRevenueTtm,
}: TopCustomersChartProps) {
  if (customers.length === 0) {
    return (
      <div className="border-border bg-surface text-muted-foreground rounded-lg border p-12 text-center text-sm">
        No customer revenue data yet.
      </div>
    );
  }

  const total = totalRevenueTtm(customers);
  const hasCompanyTotal = companyRevenueTtm > 0;
  const top10 = topNRevenue(customers, 10);
  const top10ShareOfShown = topNShare(customers, total, 10);
  const top10ShareOfCompany = topNShare(customers, companyRevenueTtm, 10);
  const steep = steepDeclinerCount(customers);
  const growers = strongGrowerCount(customers);
  const quiet = goneQuietCount(customers);
  const eroding = marginErodingCount(customers);
  const watch = toWatchList(customers);

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-foreground text-base font-semibold">
          Top {customers.length} customers — scale, momentum &amp; margin
        </h2>
        <p className="text-muted-foreground text-sm">
          Whole company · trailing 12 months · HH-consolidated
        </p>
      </div>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
        <Kpi
          label="Top-100 revenue"
          value={formatGbpCompact(total)}
          sub={
            hasCompanyTotal
              ? `${formatPct(total / companyRevenueTtm)} of ${formatGbpCompact(companyRevenueTtm)} total`
              : "trailing 12m"
          }
        />
        {hasCompanyTotal ? (
          <Kpi
            label="Top-10 share of total"
            value={formatPct(top10ShareOfCompany)}
            sub={`${formatGbpCompact(top10)} · ${formatPct(top10ShareOfShown)} of top 100`}
          />
        ) : (
          <Kpi
            label="Top-10 share of top 100"
            value={formatPct(top10ShareOfShown)}
            sub={formatGbpCompact(top10)}
          />
        )}
        <Kpi
          label="Steep decliners"
          value={String(steep)}
          sub="YoY < −40%"
          tone="bad"
        />
        <Kpi
          label="Strong growers"
          value={String(growers)}
          sub="YoY > +50%"
          tone="good"
        />
        <Kpi
          label="Gone quiet"
          value={String(quiet)}
          sub=">90d since order"
          tone="bad"
        />
        <Kpi
          label="Margin eroding"
          value={String(eroding)}
          sub=">10pp drop"
          tone="bad"
        />
      </div>

      <ScatterPanel customers={customers} />

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <ParetoPanel
          customers={customers}
          companyRevenueTtm={companyRevenueTtm}
        />
        <WatchListPanel rows={watch} />
      </div>
    </div>
  );
}

function formatPct(share: number): string {
  return `${Math.round(100 * share)}%`;
}

function ScatterPanel({ customers }: { customers: InsightsTopCustomerRow[] }) {
  const [segment, setSegment] = useState<SegmentFilter>("all");

  // Only customers with a prior-year comparison plot; cap the y for readability;
  // filter by the selected segment (client toggle over the enriched field).
  const points = useMemo(
    () =>
      toScatterPoints(customers)
        .filter((p) => p.yoyPct != null)
        .filter((p) => segment === "all" || p.segment === segment)
        .map((p) => ({
          ...p,
          yoyCapped: Math.min(YOY_CAP, p.yoyPct as number),
          // Code label for the biggest accounts only (mockup: rev > £250k).
          bigLabel: p.revenueTtm > 250_000 ? p.code : "",
        })),
    [customers, segment],
  );

  return (
    <div className="border-border bg-surface rounded-lg border p-4">
      <h3 className="text-foreground text-sm font-semibold">
        Scale vs momentum
      </h3>
      <p className="text-muted-foreground mb-3 text-xs">
        x = 12-month revenue · y = year-on-year growth · bubble = last-90-day
        revenue. Bottom-right — big accounts shrinking — is the watch zone.
      </p>

      <div className="mb-2 flex flex-wrap items-center gap-2">
        <div
          role="group"
          aria-label="Segment filter"
          className="bg-muted inline-flex gap-0.5 rounded-md p-0.5"
        >
          {SEGMENT_FILTERS.map((s) => (
            <button
              key={s.value}
              type="button"
              aria-pressed={segment === s.value}
              onClick={() => setSegment(s.value)}
              className={
                segment === s.value
                  ? "bg-surface text-foreground rounded px-3 py-1 text-xs font-medium shadow-sm"
                  : "text-muted-foreground hover:text-foreground rounded px-3 py-1 text-xs font-medium"
              }
            >
              {s.label}
            </button>
          ))}
        </div>
      </div>

      <ResponsiveContainer width="100%" height={360}>
        <ScatterChart margin={{ top: 8, right: 12, bottom: 16, left: 0 }}>
          <CartesianGrid stroke="var(--color-border)" />
          {/* Watch-zone shading: big (rev > £100k) & shrinking (YoY < 0). */}
          <ReferenceArea
            x1={WATCH_ZONE_REVENUE}
            y1={-100}
            y2={0}
            ifOverflow="extendDomain"
            fill="var(--color-destructive)"
            fillOpacity={0.06}
            stroke="none"
            label={{
              value: "big & shrinking — watch",
              position: "insideBottomRight",
              fontSize: 10,
              fill: "var(--color-muted-foreground)",
            }}
          />
          {/* Companion label for the growing half of the big-account column. */}
          <ReferenceArea
            x1={WATCH_ZONE_REVENUE}
            y1={0}
            y2={YOY_CAP}
            ifOverflow="extendDomain"
            fill="transparent"
            stroke="none"
            label={{
              value: "big & growing",
              position: "insideTopRight",
              fontSize: 10,
              fill: "var(--color-muted-foreground)",
            }}
          />
          <XAxis
            type="number"
            dataKey="revenueTtm"
            scale="log"
            domain={["auto", "auto"]}
            tickFormatter={formatGbpCompact}
            tick={{ fontSize: 10, fill: "var(--color-muted-foreground)" }}
            axisLine={{ stroke: "var(--color-border)" }}
            tickLine={false}
            name="Revenue (12m)"
          />
          <YAxis
            type="number"
            dataKey="yoyCapped"
            domain={[-100, YOY_CAP]}
            tickFormatter={(v: number) => `${v > 0 ? "+" : ""}${v}%`}
            tick={{ fontSize: 10, fill: "var(--color-muted-foreground)" }}
            axisLine={false}
            tickLine={false}
            width={44}
            name="YoY growth"
          />
          <ZAxis
            type="number"
            dataKey="revenue90d"
            range={[30, 500]}
            name="Revenue (90d)"
          />
          <Tooltip
            cursor={{ strokeDasharray: "3 3" }}
            content={ScatterTooltip as never}
          />
          <Scatter data={points} isAnimationActive={false}>
            {points.map((p) => (
              <Cell
                key={p.code}
                fill={bubbleColor(p.yoyPct as number)}
                fillOpacity={0.72}
              />
            ))}
            <LabelList
              dataKey="bigLabel"
              position="top"
              offset={8}
              style={{
                fontSize: 10,
                fontWeight: 600,
                fill: "var(--color-muted-foreground)",
              }}
            />
          </Scatter>
        </ScatterChart>
      </ResponsiveContainer>

      <div className="text-muted-foreground mt-1 flex flex-wrap gap-3 text-xs">
        <LegendSwatch color={GROWER_COLOR} label="Grower >50%" />
        <LegendSwatch color={UP_COLOR} label="Up" />
        <LegendSwatch color={DIP_COLOR} label="Down" />
        <LegendSwatch color={DECLINE_COLOR} label="Steep <−40%" />
      </div>
    </div>
  );
}

function LegendSwatch({ color, label }: { color: string; label: string }) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <span
        className="inline-block h-2.5 w-2.5 rounded-full"
        style={{ backgroundColor: color }}
      />
      {label}
    </span>
  );
}

/** A Pareto bar as plotted and shown in its tooltip. */
interface ParetoDatum {
  code: string;
  name: string;
  revenueTtm: number;
  shareOfShownPct: number;
  shareOfTotalPct: number | null;
  cumulativePct: number | null;
  basis: ShareBasis;
}

function ParetoPanel({
  customers,
  companyRevenueTtm,
}: {
  customers: InsightsTopCustomerRow[];
  companyRevenueTtm: number;
}) {
  const [basis, setBasis] = useState<ShareBasis>("shown");
  const hasCompanyTotal = companyRevenueTtm > 0;
  // Without a company total there is nothing to toggle to.
  const effectiveBasis: ShareBasis = hasCompanyTotal ? basis : "shown";
  const bars: ParetoDatum[] = toParetoBars(
    customers,
    companyRevenueTtm,
    20,
  ).map((b) => {
    const cumulative =
      effectiveBasis === "total"
        ? b.cumulativeShareOfTotal
        : b.cumulativeShareOfShown;
    return {
      code: b.code,
      name: b.name,
      revenueTtm: b.revenueTtm,
      shareOfShownPct: 100 * b.shareOfShown,
      shareOfTotalPct: b.shareOfTotal == null ? null : 100 * b.shareOfTotal,
      cumulativePct: cumulative == null ? null : Math.round(100 * cumulative),
      basis: effectiveBasis,
    };
  });

  return (
    <div className="border-border bg-surface rounded-lg border p-4">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <h3 className="text-foreground text-sm font-semibold">
          Revenue concentration
        </h3>
        {hasCompanyTotal ? (
          <div
            role="group"
            aria-label="Share basis"
            className="bg-muted inline-flex gap-0.5 rounded-md p-0.5"
          >
            {SHARE_BASES.map((b) => (
              <button
                key={b.value}
                type="button"
                aria-pressed={basis === b.value}
                onClick={() => setBasis(b.value)}
                className={
                  basis === b.value
                    ? "bg-surface text-foreground rounded px-3 py-1 text-xs font-medium shadow-sm"
                    : "text-muted-foreground hover:text-foreground rounded px-3 py-1 text-xs font-medium"
                }
              >
                {b.label}
              </button>
            ))}
          </div>
        ) : null}
      </div>
      <p className="text-muted-foreground mb-3 text-xs">
        {effectiveBasis === "total"
          ? `Top 20 by 12-month revenue; line = cumulative share of total revenue (${formatGbpCompact(companyRevenueTtm)}, all customers ex-intercompany).`
          : "Top 20 by 12-month revenue; line = cumulative share of the top 20 (always ends at 100%)."}
      </p>
      <ResponsiveContainer width="100%" height={300}>
        <ComposedChart
          data={bars}
          margin={{ top: 8, right: 8, bottom: 0, left: 0 }}
        >
          <CartesianGrid stroke="var(--color-border)" vertical={false} />
          <XAxis
            dataKey="code"
            tick={{ fontSize: 9, fill: "var(--color-muted-foreground)" }}
            axisLine={{ stroke: "var(--color-border)" }}
            tickLine={false}
            interval={0}
            angle={-45}
            textAnchor="end"
            height={54}
          />
          <YAxis
            yAxisId="rev"
            tickFormatter={formatGbpCompact}
            tick={{ fontSize: 10, fill: "var(--color-muted-foreground)" }}
            axisLine={false}
            tickLine={false}
            width={48}
          />
          <YAxis
            yAxisId="cum"
            orientation="right"
            domain={[0, 100]}
            tickFormatter={(v: number) => `${v}%`}
            tick={{ fontSize: 10, fill: "var(--color-muted-foreground)" }}
            axisLine={false}
            tickLine={false}
            width={40}
          />
          <Tooltip
            cursor={{ fill: "var(--color-muted)", opacity: 0.3 }}
            content={ParetoTooltip as never}
          />
          <Bar
            yAxisId="rev"
            dataKey="revenueTtm"
            name="Revenue (12m)"
            fill={REVENUE_COLOR}
            radius={[2, 2, 0, 0]}
            isAnimationActive={false}
          />
          <Line
            yAxisId="cum"
            type="monotone"
            dataKey="cumulativePct"
            name="Cumulative share"
            stroke={CUM_COLOR}
            strokeWidth={2}
            dot={{ r: 2 }}
            isAnimationActive={false}
          />
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
}

function WatchListPanel({ rows }: { rows: ReturnType<typeof toWatchList> }) {
  return (
    <div className="border-border bg-surface rounded-lg border p-4">
      <h3 className="text-foreground text-sm font-semibold">
        Watch zone — big &amp; shrinking
      </h3>
      <p className="text-muted-foreground mb-3 text-xs">
        Accounts over £100k that are down year-on-year, in revenue order. The
        intervention list.
      </p>
      {rows.length === 0 ? (
        <div className="text-muted-foreground py-8 text-center text-sm">
          No large accounts are shrinking. 🎉
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-muted-foreground border-border border-b text-left text-xs uppercase">
                <th className="py-2 pr-2 font-medium">Customer</th>
                <th className="py-2 pr-2 text-right font-medium">Rev 12m</th>
                <th className="py-2 pr-2 text-right font-medium">YoY</th>
                <th className="py-2 pr-2 text-right font-medium">QoQ</th>
                <th className="py-2 font-medium">Rep</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.code} className="border-border/60 border-b">
                  <td className="text-foreground max-w-[200px] truncate py-2 pr-2">
                    {r.name}
                  </td>
                  <td className="text-foreground py-2 pr-2 text-right font-mono tabular-nums">
                    {formatGbpCompact(r.revenueTtm)}
                  </td>
                  <td className="text-destructive py-2 pr-2 text-right font-mono tabular-nums">
                    {formatSignedPct(r.yoyPct)}
                  </td>
                  <td
                    className={
                      "py-2 pr-2 text-right font-mono tabular-nums " +
                      (r.qoqPct == null
                        ? "text-muted-foreground"
                        : r.qoqPct < 0
                          ? "text-destructive"
                          : "text-chart-2")
                    }
                  >
                    {r.qoqPct == null ? "—" : formatSignedPct(r.qoqPct)}
                  </td>
                  <td className="py-2">
                    {r.rep ? (
                      <span className="bg-muted text-muted-foreground rounded px-1.5 py-0.5 text-xs">
                        {r.rep}
                      </span>
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function Kpi({
  label,
  value,
  sub,
  tone,
}: {
  label: string;
  value: string;
  sub?: string;
  tone?: "good" | "bad";
}) {
  const valueTone =
    tone === "bad"
      ? "text-destructive"
      : tone === "good"
        ? "text-chart-2"
        : "text-foreground";
  return (
    <div className="border-border bg-surface rounded-lg border p-3">
      <div className="text-muted-foreground text-xs font-medium">{label}</div>
      <div
        className={`mt-1.5 font-mono text-xl font-semibold tabular-nums ${valueTone}`}
      >
        {value}
      </div>
      {sub ? (
        <div className="text-muted-foreground mt-0.5 text-xs">{sub}</div>
      ) : null}
    </div>
  );
}

function ScatterTooltip(props: TooltipContentProps<number, string>) {
  if (!props.active || !props.payload || props.payload.length === 0)
    return null;
  const d = props.payload[0]?.payload as TopCustomerScatterPoint | undefined;
  if (!d) return null;
  const meta = [d.segment, d.rep, d.market].filter(Boolean).join(" · ");
  return (
    <div
      className="border-border bg-popover text-popover-foreground rounded-lg border px-3 py-2 text-xs shadow-lg"
      role="tooltip"
    >
      <div className="mb-0.5 max-w-[240px] font-semibold">{d.name}</div>
      {meta ? (
        <div className="text-muted-foreground mb-1 max-w-[240px]">{meta}</div>
      ) : null}
      <TooltipRow label="Revenue 12m" value={formatGbpCompact(d.revenueTtm)} />
      <TooltipRow label="Revenue 90d" value={formatGbpCompact(d.revenue90d)} />
      <TooltipRow
        label="YoY"
        value={d.yoyPct == null ? "n/a" : formatSignedPct(d.yoyPct)}
      />
      <TooltipRow
        label="QoQ"
        value={d.qoqPct == null ? "n/a" : formatSignedPct(d.qoqPct)}
      />
      <TooltipRow
        label="Margin"
        value={d.marginPct == null ? "—" : `${Math.round(d.marginPct)}%`}
      />
    </div>
  );
}

function ParetoTooltip(props: TooltipContentProps<number, string>) {
  if (!props.active || !props.payload || props.payload.length === 0)
    return null;
  const d = props.payload[0]?.payload as ParetoDatum | undefined;
  if (!d) return null;
  return (
    <div
      className="border-border bg-popover text-popover-foreground rounded-lg border px-3 py-2 text-xs shadow-lg"
      role="tooltip"
    >
      <div className="mb-1 max-w-[240px] font-semibold">{d.name}</div>
      <TooltipRow label="Revenue 12m" value={formatGbpCompact(d.revenueTtm)} />
      <TooltipRow
        label="Share of top 20"
        value={formatSharePct(d.shareOfShownPct)}
      />
      {d.shareOfTotalPct == null ? null : (
        <TooltipRow
          label="Share of total"
          value={formatSharePct(d.shareOfTotalPct)}
        />
      )}
      {d.cumulativePct == null ? null : (
        <TooltipRow
          label={
            d.basis === "total" ? "Cumulative of total" : "Cumulative of top 20"
          }
          value={`${d.cumulativePct}%`}
        />
      )}
    </div>
  );
}

/** One-decimal percent: a single customer's share of total is often under 5%. */
function formatSharePct(pct: number): string {
  return `${pct.toFixed(1)}%`;
}

function TooltipRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between gap-4">
      <span className="text-muted-foreground">{label}</span>
      <span className="font-mono tabular-nums">{value}</span>
    </div>
  );
}
